<script lang="ts">
	import Section from "#lib/web-ui/primitives/section.svelte";
	import ShieldCheckIcon from "@lucide/svelte/icons/shield-check";
	import ClockIcon from "@lucide/svelte/icons/clock";
	import HomeIcon from "@lucide/svelte/icons/home";
	import {
		getTemplateContext,
		cloudinaryUrl,
		cloudinarySrcset
	} from "../template-context.svelte";
	const templateContext = getTemplateContext();
</script>

<Section class="bg-border/50">
	<div class="grid grid-cols-1 gap-8 lg:grid-cols-2">
		<!-- Content Column -->
		<div class="flex flex-col justify-center py-16 lg:py-0">
			<div class="max-w-2xl lg:max-w-none">
				<p class="text-primary text-base/7 font-semibold">
					Why Choose {templateContext.siteName}
				</p>
				<h1
					class="text-foreground mt-2 text-4xl font-semibold tracking-tight text-pretty sm:text-5xl"
				>
					We Buy Houses Fast & Easy
				</h1>
				<p class="text-muted-foreground mt-6 text-xl/8">
					Looking for a fast house sale? Need cash for your home quickly? We buy houses in
					any condition and make the process simple for you.
				</p>
				<div class="mt-10 max-w-xl text-base/7 text-gray-600 lg:max-w-none">
					<p>
						There are many home buying companies that say "we buy houses as is, in any
						condition" but few have our proven track record. We have purchased hundreds
						of houses and have a system that minimizes the work for the home seller. We
						will only make a cash offer for your home if we can complete the transaction
						in a timely manner and simplify it for you!
					</p>
					<ul role="list" class="mt-8 space-y-8 text-gray-600">
						<li class="flex gap-x-3">
							<ClockIcon class="text-primary mt-1 size-5 flex-none" />
							<span
								><strong class="text-foreground font-semibold"
									>Fast Cash Offers.</strong
								> We can make you a cash offer within 24 hours and close in as little
								as 7 days, making your home sale quick and hassle-free.</span
							>
						</li>
						<li class="flex gap-x-3">
							<HomeIcon class="text-primary mt-1 size-5 flex-none" />
							<span
								><strong class="text-foreground font-semibold"
									>No Repairs Needed.</strong
								> We buy houses "as is" in any condition. No need to fix anything or
								spend money on repairs before selling.</span
							>
						</li>
						<li class="flex gap-x-3">
							<ShieldCheckIcon class="text-primary mt-1 size-5 flex-none" />
							<span
								><strong class="text-foreground font-semibold"
									>A+ BBB Rating.</strong
								> We have an A+ rating with the Better Business Bureau because of our
								proven process and extensive experience buying homes.</span
							>
						</li>
					</ul>
					<p class="mt-8">
						When considering working with a company to sell your home to, make sure to
						review their history and their online testimonials. Don't take a chance when
						selling what is likely your largest asset, your home.
					</p>
				</div>
			</div>
		</div>

		<!-- Image Column -->
		<div class="flex flex-col">
			<img
				src={cloudinaryUrl(templateContext.images.teamHero, { width: 768 })}
				srcset={cloudinarySrcset(templateContext.images.teamHero, 640)}
				width="640"
				height="480"
				sizes="(max-width: 1024px) 100vw, 50vw"
				alt="Trusted home buyers - Local cash home buying experts"
				class="bg-border/50 aspect-[4/3] w-full rounded-lg object-cover"
				loading="eager"
				fetchpriority="high"
			/>

			<h2 class="text-foreground pt-8 text-2xl font-bold tracking-tight">
				Trusted Home Buying Experts
			</h2>
			<p class="text-muted-foreground pt-4">
				We have purchased hundreds of houses and have a proven system that minimizes the
				work for the home seller. Our experienced team understands the local market and can
				provide you with a fair cash offer that reflects your home's true value. We handle
				all the paperwork and make the process as smooth as possible for you.
			</p>
		</div>
	</div>
</Section>
